import { Link } from '@tanstack/react-router';
import type { AdminPlaceDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '../../../shared/components/empty-state';
import { useDeletePlace } from '../api/places.mutations';

interface Props {
  places: ReadonlyArray<AdminPlaceDto>;
  isLoading?: boolean;
  /** true когда список сужен серверным фильтром — пустой результат не значит «мест нет вообще». */
  filtered?: boolean;
}

export function PlacesTable({ places, isLoading, filtered: serverFiltered }: Props) {
  const [search, setSearch] = useState('');
  const del = useDeletePlace();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return places;
    return places.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.slug.toLowerCase().includes(term) ||
        (p.addressText ?? '').toLowerCase().includes(term),
    );
  }, [places, search]);

  if (!isLoading && places.length === 0) {
    if (serverFiltered) {
      return <EmptyState title="Ничего не найдено" description="Попробуйте изменить фильтр по городу." />;
    }
    return (
      <EmptyState
        title="Мест пока нет"
        description="Создайте первое место для привязки к мероприятиям."
        cta={
          <Button asChild>
            <Link to="/places/new">Создать место</Link>
          </Button>
        }
      />
    );
  }

  const handleDelete = async (place: AdminPlaceDto) => {
    if (!confirm(`Удалить место «${place.name}»?`)) return;
    try {
      await del.mutateAsync(place.id);
      toast.success('Место удалено');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось удалить';
      toast.error(message);
    }
  };

  return (
    <div className="space-y-4">
      <Input
        placeholder="Поиск по названию, slug или адресу"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-xs"
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Название</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Адрес</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((p) => (
            <TableRow key={p.id}>
              <TableCell>
                <Link
                  to="/places/$placeId"
                  params={{ placeId: p.id }}
                  className="hover:underline font-medium"
                >
                  {p.name}
                </Link>
              </TableCell>
              <TableCell className="font-mono text-xs">{p.slug}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{p.addressText ?? '—'}</TableCell>
              <TableCell>
                {p.isActive ? (
                  <Badge variant="default">active</Badge>
                ) : (
                  <Badge variant="secondary">inactive</Badge>
                )}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => handleDelete(p)}
                  disabled={del.isPending}
                >
                  Удалить
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
